import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'fashionai_bookmarks';
const COLLECTIONS_KEY = 'fashionai_collections';

function load(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function loadBookmarks() { return load(STORAGE_KEY, []); }
function loadCollections() { return load(COLLECTIONS_KEY, []); }

export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState(loadBookmarks);
  const [collections, setCollections] = useState(loadCollections);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setBookmarks(loadBookmarks());
      if (e.key === COLLECTIONS_KEY) setCollections(loadCollections());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Bookmarks ────────────────────────────────────────────────

  const addBookmark = useCallback(({ imageUrl, prompt, designId }) => {
    setBookmarks((prev) => {
      const dup = prev.find((b) =>
        (designId && b.designId === designId) || b.imageUrl === imageUrl
      );
      if (dup) return prev;
      const next = [
        { id: Date.now(), imageUrl, prompt, designId: designId || null, savedAt: Date.now(), collectionIds: [] },
        ...prev,
      ];
      save(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const removeBookmark = useCallback((bookmarkId) => {
    setBookmarks((prev) => {
      const next = prev.filter((b) => b.id !== bookmarkId);
      save(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const isBookmarked = useCallback(
    ({ imageUrl, designId }) =>
      bookmarks.some(
        (b) => (designId && b.designId === designId) || b.imageUrl === imageUrl
      ),
    [bookmarks]
  );

  const toggleBookmark = useCallback(
    ({ imageUrl, prompt, designId }) => {
      const bm = bookmarks.find(
        (b) => (designId && b.designId === designId) || b.imageUrl === imageUrl
      );
      if (bm) removeBookmark(bm.id);
      else addBookmark({ imageUrl, prompt, designId });
    },
    [bookmarks, addBookmark, removeBookmark]
  );

  const clearAll = useCallback(() => {
    setBookmarks([]);
    save(STORAGE_KEY, []);
  }, []);

  // ── Collections ──────────────────────────────────────────────

  const addCollection = useCallback((name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const newCol = { id: Date.now(), name: trimmed, createdAt: Date.now() };
    setCollections((prev) => {
      const next = [...prev, newCol];
      save(COLLECTIONS_KEY, next);
      return next;
    });
    return newCol.id;
  }, []);

  const removeCollection = useCallback((collectionId) => {
    setCollections((prev) => {
      const next = prev.filter((c) => c.id !== collectionId);
      save(COLLECTIONS_KEY, next);
      return next;
    });
    // Remove collection from all bookmarks
    setBookmarks((prev) => {
      const next = prev.map((b) => ({
        ...b,
        collectionIds: (b.collectionIds || []).filter((id) => id !== collectionId),
      }));
      save(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const renameCollection = useCallback((collectionId, newName) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCollections((prev) => {
      const next = prev.map((c) => c.id === collectionId ? { ...c, name: trimmed } : c);
      save(COLLECTIONS_KEY, next);
      return next;
    });
  }, []);

  const addToCollection = useCallback((bookmarkId, collectionId) => {
    setBookmarks((prev) => {
      const next = prev.map((b) => {
        if (b.id !== bookmarkId) return b;
        const ids = b.collectionIds || [];
        if (ids.includes(collectionId)) return b;
        return { ...b, collectionIds: [...ids, collectionId] };
      });
      save(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const removeFromCollection = useCallback((bookmarkId, collectionId) => {
    setBookmarks((prev) => {
      const next = prev.map((b) => {
        if (b.id !== bookmarkId) return b;
        return { ...b, collectionIds: (b.collectionIds || []).filter((id) => id !== collectionId) };
      });
      save(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const isInCollection = useCallback(
    (bookmarkId, collectionId) => {
      const bm = bookmarks.find((b) => b.id === bookmarkId);
      return bm ? (bm.collectionIds || []).includes(collectionId) : false;
    },
    [bookmarks]
  );

  return {
    bookmarks,
    addBookmark,
    removeBookmark,
    isBookmarked,
    toggleBookmark,
    clearAll,
    // Collections
    collections,
    addCollection,
    removeCollection,
    renameCollection,
    addToCollection,
    removeFromCollection,
    isInCollection,
  };
}
